import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  MatAutocompleteModule
} from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { ApiService } from 'src/app/core/services/api.service';
import { AddTestRoomsDialogComponent } from '../add-test-rooms-dialog/test-rooms-dialog.component';

interface TestRoom {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-test-rooms-select',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
  ],
  templateUrl: './test-rooms-select.component.html',
  styleUrls: ['./test-rooms-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TestRoomSelectComponent),
      multi: true,
    },
  ],
})
export class TestRoomSelectComponent
  implements ControlValueAccessor, OnInit
{
  searchText: string = '';
  filteredRooms: TestRoom[] = [];
  @Output() departmentSelected = new EventEmitter<string>();
  @Input() selectedValue: string | null = null;
  @Input() required?: boolean = false;
  @Input() disabled?: boolean = false;
  selectedRoom: TestRoom | null = null;
  constructor(public ability: AbilityService, private apiService: ApiService, private dialog: MatDialog) {}
  referringRooms: TestRoom[] = [];
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: string): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  openAddTestRoomDialog() {
    this.dialog
      .open(AddTestRoomsDialogComponent, {
        width: '400px',
      })
      .afterClosed()
      .subscribe((res) => {
        this.referringRooms.push({
          viewValue: res.name,
          value: res.id,
        });
        this.selectedValue = res.id;
        this.onChange(res.id);
        this.departmentSelected.emit(res.id);
        this.selectedRoom =
          this.referringRooms.find((d) => d.value === res.id) || null;
        this.searchText = this.selectedRoom.viewValue;
      });
  }

  ngOnInit() {
    this.loadTestRooms();
  }

  loadTestRooms() {
    this.apiService
      .request({
        path: `test-rooms/dropdown`,
        method: 'GET',
      })
      .subscribe({
        next: (data) => {
          this.referringRooms = data.items.map((m) => ({
            viewValue: m.name,
            value: m.id,
          }));
          if (!this.required) {
            this.referringRooms = [
              { viewValue: 'All', value: '' },
              ...this.referringRooms,
            ];
          }
          this.filteredRooms = [...this.referringRooms];
          if (this.selectedValue) {
            const match = this.referringRooms.find(
              (d) => d.value === this.selectedValue
            );
            if (match) this.selectedRoom = match;
          }
        },
        error: (error) =>
          console.error('Error loading referringRooms', error),
        complete: () => console.log('TestRooms loaded successfully'),
      });
  }

  filterRooms(event: any) {
    const search =
      typeof event === 'string'
        ? event.toLowerCase()
        : event?.viewValue?.toLowerCase() || '';
    this.filteredRooms = this.referringRooms.filter((d) =>
      d.viewValue.toLowerCase().includes(search)
    );
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onSelectTestRoom(room: TestRoom) {
    if (!room) return;
    this.selectedRoom = room;
    this.selectedValue = room.value;
    this.onChange(room.value);
    this.departmentSelected.emit(room.value);
  }

  displayRoom(room: TestRoom): string {
    return room?.viewValue || '';
  }
}
